/**
 * Department ID constants live in @vnetwork/datacentral-core (moved from here
 * in 0.5.0); re-exported so existing call sites are unchanged.
 */

import { DEPARTMENT_IDS } from '@vnetwork/datacentral-core/domain'

export { DEPARTMENT_IDS }

/**
 * Marketing staff are split across two departments (4 and 18): filtering on
 * DEPARTMENT_IDS.MARKETING alone silently drops everyone in the newer one.
 */
const MARKETING_DEPARTMENT_IDS: number[] = [
    DEPARTMENT_IDS.MARKETING,
    DEPARTMENT_IDS.MARKETING_MKT,
]

/** Departments offered when picking the employee in charge of a contact/lead. */
export const CONTACT_ASSIGNEE_DEPARTMENT_IDS: number[] = [
    DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
    DEPARTMENT_IDS.ACCOUNT,
    DEPARTMENT_IDS.SALES,
    ...MARKETING_DEPARTMENT_IDS,
]

/** Department name → ID mapping for permission scope resolution */
export const DEPARTMENT_SCOPES = [
    {
        name: 'BOARD OF DIRECTORS',
        department_id: DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
    },
    { name: 'SALES', department_id: DEPARTMENT_IDS.SALES },
    { name: 'ACCOUNT', department_id: DEPARTMENT_IDS.ACCOUNT },
    { name: 'MARKETING', department_id: DEPARTMENT_IDS.MARKETING },
    { name: 'FINANCE', department_id: DEPARTMENT_IDS.FINANCE },
    { name: 'HUMAN RESOURCE', department_id: DEPARTMENT_IDS.HUMAN_RESOURCE },
    { name: 'TECHNOLOGY', department_id: DEPARTMENT_IDS.TECHNOLOGY },
    { name: 'DEVELOPMENT', department_id: DEPARTMENT_IDS.DEVELOPMENT },
    { name: 'SALE PARTNER', department_id: DEPARTMENT_IDS.SALE_PARTNER },
    {
        name: 'BOARD OF MANAGEMENT',
        department_id: DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
    },
] as const

/**
 * Names the canonical departments carried before the 2026-10 roster
 * standardisation. The migration renames rows (TECHNOLOGY -> "Solutions 2
 * (Technology)", ACCOUNT -> "Customer Service", ...) but keeps ids and codes, so
 * code that is keyed on a department name resolves it from the id instead.
 * Departments outside this list keep whatever name the server sends.
 */
const CANONICAL_DEPARTMENT_NAMES: Record<number, string> = {
    [DEPARTMENT_IDS.BOARD_OF_DIRECTORS]: 'BOARD OF DIRECTORS',
    [DEPARTMENT_IDS.SALES]: 'SALES',
    [DEPARTMENT_IDS.ACCOUNT]: 'ACCOUNT',
    [DEPARTMENT_IDS.MARKETING]: 'MARKETING',
    [DEPARTMENT_IDS.FINANCE]: 'FINANCE',
    [DEPARTMENT_IDS.HUMAN_RESOURCE]: 'HUMAN RESOURCE',
    [DEPARTMENT_IDS.TECHNOLOGY]: 'TECHNOLOGY',
    [DEPARTMENT_IDS.DEVELOPMENT]: 'DEVELOPMENT',
    [DEPARTMENT_IDS.SALE_PARTNER]: 'SALE PARTNER',
    // Not in DEPARTMENT_IDS: retired by the same migration, kept so staff still
    // filed there before it runs keep their access.
    10: 'BOARD OF MANAGEMENT',
}

export const getCanonicalDepartmentName = (
    employee?: { department_id?: number; department_name?: string } | null
): string =>
    (employee?.department_id !== undefined
        ? CANONICAL_DEPARTMENT_NAMES[employee.department_id]
        : undefined) ??
    employee?.department_name ??
    ''
